/**
 * FmFormControlThemeProvider — narrow adapter for AntD form controls only.
 *
 * Do not use this for page chrome, layout, cards, tables, feedback, or route
 * theming. FmThemeProvider owns route data-theme; this adapter only keeps the
 * still-permitted AntD form inputs aligned to Lehti AI brand tokens.
 */
import React, { useLayoutEffect } from 'react';
import { ConfigProvider, theme as antTheme } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/fi';
import 'dayjs/locale/sv';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import fiFI from 'antd/locale/fi_FI';
import svSE from 'antd/locale/sv_SE';
import {
  forestDarkFormControlTheme,
  forestFormControlTheme,
  forestInheritFormControlTheme,
  type RouteTheme,
} from '@/shared/config/designTokens';
import type { Language } from '@/i18n/types';

interface FmFormControlThemeProviderProps {
  children: React.ReactNode;
  theme?: RouteTheme | 'inherit';
  language?: Language;
}

const FORM_LOCALES = { en: enUS, es: esES, fi: fiFI, sv: svSE } as const;
const DAYJS_LOCALES = { en: 'en', es: 'es', fi: 'fi', sv: 'sv' } as const;

export const FmFormControlThemeProvider: React.FC<FmFormControlThemeProviderProps> = ({
  children,
  theme = 'light',
  language,
}) => {
  /* `inherit` means "do not decide the surface", never "do not brand the
     control": without the brand tokens every checkbox, radio, switch and picker
     under this provider fell back to AntD's own blue.
     */
  const antdTheme = theme === 'inherit'
    ? forestInheritFormControlTheme
    : theme === 'dark'
      ? { algorithm: antTheme.darkAlgorithm, ...forestDarkFormControlTheme }
      : { algorithm: antTheme.defaultAlgorithm, ...forestFormControlTheme };

  // AntD's locale names the picker's buttons; the calendar's month and weekday
  // names come from the dayjs locale, which has to follow the same language.
  // dayjs's locale is process-global, so it is set as a commit-phase side
  // effect (before paint, so no picker panel can open with the old names)
  // rather than mutated during render.
  useLayoutEffect(() => {
    if (language && dayjs.locale() !== DAYJS_LOCALES[language]) dayjs.locale(DAYJS_LOCALES[language]);
  }, [language]);

  return (
    <ConfigProvider theme={antdTheme} locale={language ? FORM_LOCALES[language] : undefined}>
      {children}
    </ConfigProvider>
  );
};

export default FmFormControlThemeProvider;
